import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';

interface A11yIssue {
  type: 'error' | 'warning' | 'info';
  category: string;
  message: string;
  element?: string;
}

/**
 * Development tool to check accessibility issues in real-time
 * Only renders in development mode
 */
export const AccessibilityChecker = () => {
  const [issues, setIssues] = useState<A11yIssue[]>([]);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    if (import.meta.env.PROD) return;

    const checkAccessibility = () => {
      const foundIssues: A11yIssue[] = [];

      // Check touch target sizes (≥44px)
      const interactiveElements = document.querySelectorAll('button, a[href], input, select, textarea, [role="button"]');
      interactiveElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.height > 0 && rect.height < 44) {
          foundIssues.push({
            type: 'error',
            category: 'Touch Target',
            message: `Element too small: ${Math.round(rect.height)}px height (minimum 44px)`,
            element: el.tagName.toLowerCase()
          });
        }
      });

      // Check for missing alt text
      const images = document.querySelectorAll('img:not([alt])');
      if (images.length > 0) {
        foundIssues.push({
          type: 'error',
          category: 'Image Alt Text',
          message: `${images.length} image(s) missing alt attribute`,
        });
      }

      // Check for proper heading hierarchy
      const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
      const headingLevels = headings.map(h => parseInt(h.tagName[1]));
      for (let i = 1; i < headingLevels.length; i++) {
        if (headingLevels[i] - headingLevels[i - 1] > 1) {
          foundIssues.push({
            type: 'warning',
            category: 'Heading Order',
            message: `Heading hierarchy skip: h${headingLevels[i - 1]} to h${headingLevels[i]}`,
          });
        }
      }

      // Check for buttons without labels
      const unlabeledButtons = document.querySelectorAll('button:not([aria-label]):empty, [role="button"]:not([aria-label]):empty');
      if (unlabeledButtons.length > 0) {
        foundIssues.push({
          type: 'error',
          category: 'Button Labels',
          message: `${unlabeledButtons.length} button(s) without text or aria-label`,
        });
      }

      // Check for form inputs without labels
      const inputs = document.querySelectorAll('input:not([type="hidden"]), textarea, select');
      inputs.forEach((input) => {
        const id = input.getAttribute('id');
        const hasLabel = id && document.querySelector(`label[for="${id}"]`);
        const hasAriaLabel = input.getAttribute('aria-label') || input.getAttribute('aria-labelledby');
        
        if (!hasLabel && !hasAriaLabel) {
          foundIssues.push({
            type: 'error',
            category: 'Form Labels',
            message: 'Input missing associated label',
            element: input.tagName.toLowerCase()
          });
        }
      });

      setIssues(foundIssues);
      setIsChecking(false);
    };

    // Run check after initial render
    const timer = setTimeout(() => {
      setIsChecking(true);
      checkAccessibility();
    }, 1000);

    // Recheck on DOM mutations (debounced)
    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      setTimeout(() => {
        setIsChecking(true);
        checkAccessibility();
      }, 500);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false
    });

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  if (import.meta.env.PROD) return null;

  const errors = issues.filter(i => i.type === 'error');
  const warnings = issues.filter(i => i.type === 'warning');
  const totalIssues = errors.length + warnings.length;

  return (
    <Card className="fixed bottom-4 right-4 z-50 w-96 p-4 shadow-lg">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Accessibility Check</h3>
          {isChecking ? (
            <span className="text-xs text-muted-foreground">Checking...</span>
          ) : (
            <div className="flex items-center gap-2">
              {totalIssues === 0 ? (
                <>
                  <CheckCircle2 size={16} className="text-green-600" />
                  <span className="text-xs text-green-600">No issues</span>
                </>
              ) : (
                <>
                  <AlertTriangle size={16} className="text-amber-600" />
                  <span className="text-xs text-amber-600">{totalIssues} issues</span>
                </>
              )}
            </div>
          )}
        </div>

        {totalIssues > 0 && (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {errors.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-semibold text-red-600 flex items-center gap-1">
                  <AlertTriangle size={12} />
                  Errors ({errors.length})
                </div>
                {errors.map((issue, idx) => (
                  <div key={idx} className="text-xs bg-red-50 p-2 rounded">
                    <div className="font-medium">{issue.category}</div>
                    <div className="text-muted-foreground">{issue.message}</div>
                  </div>
                ))}
              </div>
            )}

            {warnings.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-semibold text-amber-600 flex items-center gap-1">
                  <Info size={12} />
                  Warnings ({warnings.length})
                </div>
                {warnings.map((issue, idx) => (
                  <div key={idx} className="text-xs bg-amber-50 p-2 rounded">
                    <div className="font-medium">{issue.category}</div>
                    <div className="text-muted-foreground">{issue.message}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-muted-foreground border-t pt-2">
          <div className="font-semibold mb-1">Quick Checks:</div>
          <ul className="space-y-0.5 text-[10px]">
            <li>✓ Touch targets ≥44px</li>
            <li>✓ Images have alt text</li>
            <li>✓ Buttons have labels</li>
            <li>✓ Forms have labels</li>
            <li>✓ Heading hierarchy</li>
          </ul>
        </div>
      </div>
    </Card>
  );
};
